import { useCallback } from "react";
import { Edit3, ExternalLink, FileText, Globe2, RefreshCw } from "lucide-react";
import { api } from "../../services/api";
import { formatUsDateTime } from "../../utils/timezone";
import { useAdminResource } from "./useAdminResource";

const fallbackPayload = { pages: [] };

function formatDate(value) {
  return formatUsDateTime(value, "Not updated");
}

export default function CmsPages() {
  const loadPages = useCallback(() => api.get("/cms/admin/pages"), []);
  const { data, error, loading, reload } = useAdminResource(loadPages, fallbackPayload);
  const pages = data.pages || [];

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-[#4391d1]">Website CMS</p>
          <h1 className="mt-2 text-3xl font-black text-gray-900">Editable Website Pages</h1>
          <p className="mt-2 max-w-2xl text-sm font-semibold leading-6 text-gray-500">
            Open a live website page in secure edit mode and update static headings, paragraphs, CTA labels, and policy copy directly where they appear.
          </p>
        </div>
        <button
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#1278b5]"
          onClick={reload}
          type="button"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-bold text-amber-800">
          {error}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        {pages.map((page) => {
          const editableSections = (page.sections || []).filter((section) => section.enabled !== false).length;
          const editUrl = `${page.path}${page.path.includes("?") ? "&" : "?"}cmsEdit=1`;

          return (
            <article key={page.slug} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-black uppercase text-[#1278b5]">
                    <Globe2 className="h-3.5 w-3.5" />
                    {page.path}
                  </div>
                  <h2 className="text-xl font-black text-gray-900">{page.title}</h2>
                  <p className="mt-2 text-sm font-semibold text-gray-500">
                    {editableSections ? `${editableSections} editable section${editableSections === 1 ? "" : "s"}` : "No static CMS fields yet"}
                  </p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-black uppercase ${page.status === "published" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                  {page.status}
                </span>
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
                <div className="inline-flex items-center gap-2 text-xs font-bold text-gray-400">
                  <FileText className="h-4 w-4" />
                  Updated {formatDate(page.updatedAt)}
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#1278b5]"
                    href={page.path}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View
                  </a>
                  <a
                    className="inline-flex items-center gap-2 rounded-xl bg-[#4391d1] px-4 py-2 text-sm font-black text-white hover:bg-blue-600"
                    href={editUrl}
                  >
                    <Edit3 className="h-4 w-4" />
                    Edit
                  </a>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {!loading && !pages.length && (
        <div className="rounded-2xl border border-gray-100 bg-white p-8 text-center text-sm font-bold text-gray-500">
          No CMS pages found. Visit this page again after the server seeds CMS defaults.
        </div>
      )}
    </div>
  );
}
